import type { Playlist, PlaylistSummary, SearchResult } from "radio-shared";
import { auth } from "./firebase";

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(path, {
    ...init,
    headers: {
      // Seulement quand on envoie vraiment quelque chose : annoncer un JSON
      // sans corps — le cas des DELETE — fait échouer la requête sur le parseur
      // de Fastify, avant même d'arriver à l'authentification.
      ...(init.body === undefined || init.body instanceof FormData
        ? {}
        : { "content-type": "application/json" }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error((detail as { error?: string }).error ?? `Erreur ${res.status}`);
  }
  return res.json() as Promise<T>;
}

const json = (body: unknown): RequestInit => ({ method: "POST", body: JSON.stringify(body) });
const form = (file: Blob, name: string): RequestInit => {
  const data = new FormData();
  data.append("file", file, name);
  return { method: "POST", body: data };
};

export const api = {
  time: () => call<{ serverMs: number }>("/api/time"),
  heartbeat: () => call<{ ok: true }>("/api/heartbeat", json({})),
  addTrack: (url: string) => call<{ trackId: string }>("/api/tracks", json({ url })),
  searchYouTube: (q: string) =>
    call<{ results: SearchResult[] }>(`/api/youtube/search?q=${encodeURIComponent(q)}`),
  searchPlaylists: (q: string) =>
    call<{ playlists: PlaylistSummary[] }>(`/api/youtube/playlists?q=${encodeURIComponent(q)}`),
  openPlaylist: (list: string) =>
    call<Playlist>(`/api/youtube/playlist?list=${encodeURIComponent(list)}`),
  addTracks: (playlistId: string, videoIds: string[]) =>
    call<{ added: number; skipped: number }>("/api/tracks/batch", json({ playlistId, videoIds })),
  upload: (file: File) => call<{ trackId: string }>("/api/tracks/upload", form(file, file.name)),
  advance: (expectedTrackId: string | null) =>
    call<{ kind: string }>("/api/advance", json({ expectedTrackId })),
  kick: () => call<{ kind: string }>("/api/kick", json({})),
  skip: () => call<{ kind: string; votes?: number; needed?: number }>("/api/skip", json({})),
  like: (trackId: string) =>
    call<{ liked: boolean; count: number }>(`/api/tracks/${trackId}/like`, json({})),
  mic: (clip: Blob) => call<{ trackId: string; onAir: boolean }>("/api/mic", form(clip, "clip.webm")),
  audioUrl: (trackId: string) => call<{ url: string }>(`/api/audio/${trackId}`),
  sendMessage: (text: string) => call<{ messageId: string }>("/api/chat", json({ text })),
  deleteMessage: (id: string) => call<{ ok: true }>(`/api/chat/${id}`, { method: "DELETE" }),
  removeTrack: (trackId: string) => call<{ ok: true }>(`/api/tracks/${trackId}`, { method: "DELETE" }),
};
